import React from 'react';
import { Link } from 'react-router-dom';
import { octoberWebsiteHtml } from './octoberWebsiteContent';
import { captureOutboundLinkClick } from '../utils/analytics';
import { NEWSLETTER_SUBSCRIBE_URL } from './newsletterIssues';
import './NewsletterOctober2026Page.css';

export default function NewsletterOctober2026Page() {
  return (
    <main className="newsletter-october bg-[#fbfcfc] text-[#33413e]">
      <article>
        <header className="border-b border-[#d6e5e6] bg-[#eaf5f5] px-5 py-10 sm:px-8 md:py-[52px]">
          <div className="hero-animate mx-auto max-w-[940px]">
            <nav aria-label="Breadcrumb" className="mb-7 flex flex-wrap items-center gap-2 text-[13px] font-medium text-[#476c70]">
              <Link className="hover:text-[#075eaf]" to="/resources">Resources</Link>
              <span aria-hidden="true" className="text-[#86a4a6]">/</span><Link to="/resources/newsletter" aria-label="MedHug Newsletter archive" className="hover:text-[#075eaf]">Newsletter</Link>
              <span aria-hidden="true" className="text-[#86a4a6]">/</span><span>October 2026</span>
            </nav>
            <p className="mb-3 text-[12px] font-bold uppercase tracking-[0.19em] text-[#12646b]">Volume 2&nbsp; • &nbsp;October 2026</p>
            <h1 className="max-w-[890px] text-[36px] font-semibold leading-[1.07] tracking-[-0.045em] text-[#14201d] sm:text-[44px] md:text-[52px]">Ozempic and Wegovy: Medicare Coverage and Patient Costs in 2027</h1>
            <p className="mt-5 max-w-[760px] text-[17px] leading-7 text-[#354c48] md:text-[19px] md:leading-8">Ozempic and Wegovy cost comparisons, plan premiums updates, and new MedHug features.</p>
            <div className="mt-7 flex flex-col items-start gap-4 border-t border-[#c9dddd] pt-5 sm:flex-row sm:items-center sm:justify-between">
              <a
                href="https://www.linkedin.com/in/mgblyumin/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View Michael Blyumin's LinkedIn profile"
                className="group inline-flex items-center gap-3"
                onClick={() => captureOutboundLinkClick('newsletter-author-linkedin', 'https://www.linkedin.com/in/mgblyumin/', { placement: 'newsletter-1026-hero' })}
              >
                <img
                  src="/images/michael-blyumin.png"
                  alt="Michael Blyumin"
                  width="56"
                  height="56"
                  className="h-14 w-14 shrink-0 rounded-full border-2 border-white object-cover shadow-sm transition-transform duration-200 group-hover:scale-[1.03]"
                />
                <span>
                  <span className="block text-[14px] font-semibold leading-5 text-[#1f3430]">
                    Written by Michael Blyumin, PharmD, APh, BCACP, CDCES
                  </span>
                  <svg
                    aria-hidden="true"
                    className="mt-1 h-4 w-4 text-[#0a66c2] transition-colors group-hover:text-[#03499f]"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </span>
              </a>
              <a
                href={NEWSLETTER_SUBSCRIBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-[#8bb6cf] bg-white/55 px-4 py-2 text-[13px] font-semibold text-[#075eaf] hover:border-[#075eaf] hover:bg-white"
                onClick={() => captureOutboundLinkClick('newsletter-subscribe-top', NEWSLETTER_SUBSCRIBE_URL, { placement: 'newsletter-1026-hero' })}
              >
                Subscribe to MedHug Newsletter
                <span aria-hidden="true" className="ml-2">→</span>
              </a>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[860px] px-5 py-8 sm:px-8 md:py-12">
          <section aria-labelledby="issue-heading" className="rounded-2xl border border-[#d6e3e1] bg-white p-6 shadow-[0_8px_28px_rgba(24,66,62,0.05)] md:p-8">
            <h2 id="issue-heading" className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#31616b]">In this issue</h2>
            <ol className="mt-5 grid gap-5 md:grid-cols-3 md:gap-7">
              {[
                ['01', 'coverage-overview', '2027 Medicare coverage', 'Deductible, out-of-pocket cap, and plan premiums.'],
                ['02', 'ozempic', 'Ozempic and Wegovy costs', 'Coverage comparisons and monthly patient costs.'],
                ['03', 'product-updates', 'MedHug product updates', 'New plan information and medication comparisons.'],
              ].map(([number, id, title, summary]) => (
                <li key={id}><a href={`#${id}`} className="block rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#075eaf]"><p className="text-[12px] font-semibold text-[#075eaf]">{number}</p><p className="mt-2 text-[16px] font-semibold text-[#1d2926]">{title}</p><p className="mt-1 text-[14px] leading-6 text-[#5a6864]">{summary}</p></a></li>
              ))}
            </ol>
          </section>
          {/* Trusted checked-in content, adapted at build time from the approved newsletter HTML. */}
          <div className="october-content" data-testid="october-newsletter-content" dangerouslySetInnerHTML={{ __html: octoberWebsiteHtml }} />
          <section className="mt-8 overflow-hidden rounded-3xl bg-[#163238] px-6 py-9 text-white md:px-10 md:py-11">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9fcdd0]">MedHug Newsletter</p>
            <div className="mt-3 flex flex-col gap-7 md:flex-row md:items-end md:justify-between">
              <div className="max-w-xl">
                <h2 className="text-[30px] font-semibold leading-tight tracking-[-0.03em] text-white md:text-[36px]">Get the next clinical update in your inbox.</h2>
                <p className="mt-4 text-[16px] leading-7 text-[#c8d9da]">Practical Medicare coverage and medication-access guidance for healthcare professionals.</p>
              </div>
              <a
                href={NEWSLETTER_SUBSCRIBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center self-start rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-[#17383d] hover:bg-[#eaf4f4] md:self-auto"
                onClick={() => captureOutboundLinkClick('newsletter-subscribe-bottom', NEWSLETTER_SUBSCRIBE_URL, { placement: 'newsletter-1026-end' })}
              >
                Subscribe to MedHug Newsletter
                <span aria-hidden="true" className="ml-2">→</span>
              </a>
            </div>
          </section>


          <footer className="pt-10 text-[14px] leading-7 text-[#5a6864]"><Link to="/resources/newsletter" className="text-[#075eaf] underline underline-offset-4">Explore the newsletter archive</Link></footer>
        </div>
      </article>
    </main>
  );
}
